import type { LocalizedArticle } from './types';

export const qcArticle: LocalizedArticle = {
  en: {
    seoTitle: 'USFans QC Guide: Quality Check Explained 2026',
    metaDescription:
      'Learn what QC means, how to read QC photos, what to check before shipping, and how quality checks help you buy with more confidence.',
    h1: 'USFans QC Guide: Quality Check Explained',
    intro:
      'QC, short for quality check, is one of the most useful tools available to shoppers who use the USFans spreadsheet. This guide explains what QC is, how to read QC photos and notes, and what to look for before an item ships.',
    sections: [
      {
        heading: 'What QC Means',
        body: [
          'QC stands for quality check. In this context, it usually refers to photos or notes that help you evaluate a product before it is shipped to you.',
          'A good QC process can reveal details that the main product listing does not show, such as stitching, materials and overall condition.',
        ],
      },
      {
        heading: 'How to Read QC Photos',
        body: [
          'Look at photos from multiple angles rather than a single shot. Check the stitching, labels, colors and any areas that are easy to miss in the main listing.',
          'Compare the QC photos against the product description. If something looks different, ask the seller before you confirm the order.',
        ],
      },
      {
        heading: 'What to Check Before Shipping',
        body: [
          'Confirm the item matches its description, that the correct size or color is shown, and that there are no obvious defects visible in the photos.',
          'If a detail is unclear, request an additional photo or a closer view of the area you are concerned about.',
        ],
      },
      {
        heading: 'Using QC to Buy Confidently',
        body: [
          'QC is not a guarantee, but it significantly reduces uncertainty. Sellers who provide clear QC details are often easier to evaluate.',
          'If QC is unavailable, proceed with extra caution and ask for it. USFansSheet.link is an independent resource and does not guarantee any seller or product.',
        ],
      },
    ],
    table: {
      headers: ['QC Detail', 'What to Look For'],
      rows: [
        ['Multiple angles', 'Photos from several viewpoints'],
        ['Stitching', 'Check seams and finishing'],
        ['Labels', 'Verify branding and tags'],
        ['Color match', 'Compare against the description'],
        ['Condition', 'Look for visible defects'],
      ],
    },
    pros: [
      'Explains QC in plain language',
      'Practical photo-reading tips',
      'Helps you buy more confidently',
      'Independent and neutral guidance',
    ],
    cons: [
      'QC is not a guarantee',
      'Not all sellers provide QC',
      'Photos may not show everything',
    ],
    faq: [
      { q: 'What does QC mean?', a: 'QC stands for quality check. It refers to photos or notes that help you evaluate a product before it ships.' },
      { q: 'How do I read QC photos?', a: 'Review photos from multiple angles, check stitching, labels and colors, and compare them against the product description.' },
      { q: 'What should I check before shipping?', a: 'Confirm the item matches its description, the correct size or color is shown, and there are no obvious defects.' },
      { q: 'Does QC guarantee a good product?', a: 'No. QC reduces uncertainty but is not a guarantee, and not all sellers provide it.' },
    ],
  },
  es: {
    seoTitle: 'Guía de QC de USFans: Control de Calidad Explicado 2026',
    metaDescription:
      'Aprende qué significa QC, cómo leer las fotos de QC, qué revisar antes del envío y cómo los controles de calidad te ayudan a comprar con más confianza.',
    h1: 'Guía de QC de USFans: Control de Calidad Explicado',
    intro:
      'QC, abreviatura de control de calidad, es una de las herramientas más útiles para los compradores que usan la USFans spreadsheet. Esta guía explica qué es el QC, cómo leer las fotos y notas de QC y qué buscar antes de que un artículo se envíe.',
    sections: [
      {
        heading: 'Qué Significa QC',
        body: [
          'QC significa control de calidad. En este contexto, suele referirse a fotos o notas que te ayudan a evaluar un producto antes de que se te envíe.',
          'Un buen proceso de QC puede revelar detalles que el listado principal no muestra, como costuras, materiales y estado general.',
        ],
      },
      {
        heading: 'Cómo Leer las Fotos de QC',
        body: [
          'Mira las fotos desde varios ángulos en lugar de una sola toma. Revisa las costuras, etiquetas, colores y las zonas fáciles de pasar por alto.',
          'Compara las fotos de QC con la descripción del producto. Si algo se ve distinto, pregunta al vendedor antes de confirmar el pedido.',
        ],
      },
      {
        heading: 'Qué Revisar Antes del Envío',
        body: [
          'Confirma que el artículo coincide con su descripción, que se muestra la talla o el color correctos y que no hay defectos evidentes en las fotos.',
          'Si un detalle no está claro, pide una foto adicional o una vista más cercana de la zona que te preocupa.',
        ],
      },
      {
        heading: 'Usar el QC para Comprar con Confianza',
        body: [
          'El QC no es una garantía, pero reduce mucho la incertidumbre. Los vendedores que ofrecen detalles claros de QC suelen ser más fáciles de evaluar.',
          'Si el QC no está disponible, procede con precaución y pídelo. USFansSheet.link es un recurso independiente y no garantiza a ningún vendedor ni producto.',
        ],
      },
    ],
    table: {
      headers: ['Detalle de QC', 'Qué Buscar'],
      rows: [
        ['Múltiples ángulos', 'Fotos desde varios puntos de vista'],
        ['Costuras', 'Revisa las costuras y acabados'],
        ['Etiquetas', 'Verifica la marca y las etiquetas'],
        ['Coincidencia de color', 'Compara con la descripción'],
        ['Estado', 'Busca defectos visibles'],
      ],
    },
    pros: [
      'Explica el QC en lenguaje sencillo',
      'Consejos prácticos para leer fotos',
      'Ayuda a comprar con más confianza',
      'Guía independiente y neutral',
    ],
    cons: [
      'El QC no es una garantía',
      'No todos los vendedores ofrecen QC',
      'Las fotos pueden no mostrarlo todo',
    ],
    faq: [
      { q: '¿Qué significa QC?', a: 'QC significa control de calidad. Se refiere a fotos o notas que te ayudan a evaluar un producto antes de que se envíe.' },
      { q: '¿Cómo leo las fotos de QC?', a: 'Revisa fotos desde varios ángulos, comprueba costuras, etiquetas y colores, y compáralas con la descripción del producto.' },
      { q: '¿Qué debo revisar antes del envío?', a: 'Confirma que el artículo coincide con su descripción, que se muestra la talla o color correctos y que no hay defectos evidentes.' },
      { q: '¿El QC garantiza un buen producto?', a: 'No. El QC reduce la incertidumbre, pero no es una garantía y no todos los vendedores lo ofrecen.' },
    ],
  },
  fr: {
    seoTitle: 'Guide QC USFans : Le Contrôle Qualité Expliqué 2026',
    metaDescription:
      'Découvrez ce que signifie QC, comment lire les photos QC, ce qu\'il faut vérifier avant l\'expédition et comment les contrôles qualité aident à acheter en confiance.',
    h1: 'Guide QC USFans : Le Contrôle Qualité Expliqué',
    intro:
      'QC, abréviation de contrôle qualité, est l\'un des outils les plus utiles pour les acheteurs qui utilisent la USFans spreadsheet. Ce guide explique ce qu\'est le QC, comment lire les photos et notes QC et ce qu\'il faut rechercher avant l\'expédition d\'un article.',
    sections: [
      {
        heading: 'Ce Que Signifie QC',
        body: [
          'QC signifie contrôle qualité. Dans ce contexte, il s\'agit généralement de photos ou de notes qui vous aident à évaluer un produit avant son expédition.',
          'Un bon processus de QC peut révéler des détails que la fiche principale ne montre pas, comme les coutures, les matériaux et l\'état général.',
        ],
      },
      {
        heading: 'Comment Lire les Photos QC',
        body: [
          'Regardez les photos sous plusieurs angles plutôt qu\'une seule prise. Vérifiez les coutures, les étiquettes, les couleurs et les zones faciles à manquer.',
          'Comparez les photos QC avec la description du produit. Si quelque chose semble différent, demandez au vendeur avant de confirmer la commande.',
        ],
      },
      {
        heading: 'Ce qu\'il Faut Vérifier Avant l\'Expédition',
        body: [
          'Confirmez que l\'article correspond à sa description, que la bonne taille ou couleur est montrée et qu\'il n\'y a pas de défauts évidents sur les photos.',
          'Si un détail n\'est pas clair, demandez une photo supplémentaire ou une vue plus proche de la zone qui vous préoccupe.',
        ],
      },
      {
        heading: 'Utiliser le QC pour Acheter en Confiance',
        body: [
          'Le QC n\'est pas une garantie, mais il réduit considérablement l\'incertitude. Les vendeurs qui fournissent des détails QC clairs sont souvent plus faciles à évaluer.',
          'Si le QC n\'est pas disponible, procédez avec prudence et demandez-le. USFansSheet.link est une ressource indépendante et ne garantit aucun vendeur ni produit.',
        ],
      },
    ],
    table: {
      headers: ['Détail QC', 'Ce qu\'il Faut Rechercher'],
      rows: [
        ['Plusieurs angles', 'Des photos sous plusieurs points de vue'],
        ['Coutures', 'Vérifiez les coutures et finitions'],
        ['Étiquettes', 'Vérifiez la marque et les étiquettes'],
        ['Correspondance couleur', 'Comparez avec la description'],
        ['État', 'Recherchez des défauts visibles'],
      ],
    },
    pros: [
      'Explique le QC en langage simple',
      'Conseils pratiques pour lire les photos',
      'Aide à acheter en confiance',
      'Guide indépendant et neutre',
    ],
    cons: [
      'Le QC n\'est pas une garantie',
      'Tous les vendeurs ne fournissent pas de QC',
      'Les photos peuvent ne pas tout montrer',
    ],
    faq: [
      { q: 'Que signifie QC ?', a: 'QC signifie contrôle qualité. Il s\'agit de photos ou de notes qui vous aident à évaluer un produit avant son expédition.' },
      { q: 'Comment lire les photos QC ?', a: 'Examinez les photos sous plusieurs angles, vérifiez les coutures, les étiquettes et les couleurs, et comparez-les à la description.' },
      { q: 'Que vérifier avant l\'expédition ?', a: 'Confirmez que l\'article correspond à sa description, que la bonne taille ou couleur est montrée et qu\'il n\'y a pas de défauts évidents.' },
      { q: 'Le QC garantit-il un bon produit ?', a: 'Non. Le QC réduit l\'incertitude mais n\'est pas une garantie, et tous les vendeurs ne le fournissent pas.' },
    ],
  },
  pl: {
    seoTitle: 'Przewodnik QC USFans: Kontrola Jakości Wyjaśniona 2026',
    metaDescription:
      'Dowiedz się, co oznacza QC, jak czytać zdjęcia QC, co sprawdzić przed wysyłką i jak kontrole jakości pomagają kupować pewniej.',
    h1: 'Przewodnik QC USFans: Kontrola Jakości Wyjaśniona',
    intro:
      'QC, skrót od kontroli jakości, to jedno z najbardziej przydatnych narzędzi dla kupujących korzystających z USFans spreadsheet. Ten przewodnik wyjaśnia, czym jest QC, jak czytać zdjęcia i notatki QC oraz czego szukać przed wysyłką przedmiotu.',
    sections: [
      {
        heading: 'Co Oznacza QC',
        body: [
          'QC oznacza kontrolę jakości. W tym kontekście zwykle odnosi się do zdjęć lub notatek, które pomagają ocenić produkt przed wysyłką.',
          'Dobry proces QC może ujawnić szczegóły, których nie pokazuje główna oferta, takie jak szwy, materiały i ogólny stan.',
        ],
      },
      {
        heading: 'Jak Czytać Zdjęcia QC',
        body: [
          'Oglądaj zdjęcia z różnych kątów, a nie tylko jedno ujęcie. Sprawdź szwy, etykiety, kolory i obszary łatwe do przeoczenia.',
          'Porównaj zdjęcia QC z opisem produktu. Jeśli coś wygląda inaczej, zapytaj sprzedawcę przed potwierdzeniem zamówienia.',
        ],
      },
      {
        heading: 'Co Sprawdzić Przed Wysyłką',
        body: [
          'Potwierdź, że przedmiot pasuje do opisu, że pokazano właściwy rozmiar lub kolor oraz że na zdjęciach nie ma widocznych wad.',
          'Jeśli jakiś szczegół jest niejasny, poproś o dodatkowe zdjęcie lub bliższy widok obszaru, który Cię martwi.',
        ],
      },
      {
        heading: 'Używanie QC do Pewnych Zakupów',
        body: [
          'QC nie jest gwarancją, ale znacznie zmniejsza niepewność. Sprzedawcy, którzy podają jasne szczegóły QC, są często łatwiejsi do oceny.',
          'Jeśli QC nie jest dostępne, postępuj ostrożnie i poproś o nie. USFansSheet.link to niezależne źródło i nie gwarantuje żadnego sprzedawcy ani produktu.',
        ],
      },
    ],
    table: {
      headers: ['Szczegół QC', 'Czego Szukać'],
      rows: [
        ['Wiele kątów', 'Zdjęcia z kilku punktów widzenia'],
        ['Szwy', 'Sprawdź szwy i wykończenie'],
        ['Etykiety', 'Zweryfikuj markę i metki'],
        ['Dopasowanie koloru', 'Porównaj z opisem'],
        ['Stan', 'Szukaj widocznych wad'],
      ],
    },
    pros: [
      'Wyjaśnia QC prostym językiem',
      'Praktyczne wskazówki czytania zdjęć',
      'Pomaga kupować pewniej',
      'Niezależne i neutralne wskazówki',
    ],
    cons: [
      'QC nie jest gwarancją',
      'Nie wszyscy sprzedawcy oferują QC',
      'Zdjęcia mogą nie pokazywać wszystkiego',
    ],
    faq: [
      { q: 'Co oznacza QC?', a: 'QC oznacza kontrolę jakości. Odnosi się do zdjęć lub notatek, które pomagają ocenić produkt przed wysyłką.' },
      { q: 'Jak czytać zdjęcia QC?', a: 'Przeglądaj zdjęcia z różnych kątów, sprawdzaj szwy, etykiety i kolory oraz porównuj je z opisem produktu.' },
      { q: 'Co sprawdzić przed wysyłką?', a: 'Potwierdź, że przedmiot pasuje do opisu, że pokazano właściwy rozmiar lub kolor oraz że nie ma widocznych wad.' },
      { q: 'Czy QC gwarantuje dobry produkt?', a: 'Nie. QC zmniejsza niepewność, ale nie jest gwarancją i nie wszyscy sprzedawcy je oferują.' },
    ],
  },
};